import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import {
  DateFilterOperator,
  KnownDateFilterTimeFrame,
} from "@redotech/redo-views-common/advanced-filters/date-filter";
import { ExistsFilterOperator } from "@redotech/redo-views-common/advanced-filters/exists-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { StringFilterOperator } from "@redotech/redo-views-common/advanced-filters/string-filter";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "./date-schema";
import { FulfillmentGroupStatus } from "./fulfillment-group-status";

/**
 * Carriers whose manifest window follows the merchant's own calendar day.
 * Everyone else is pinned to the global UTC sweep, which their cutoff must clear.
 */
export const MERCHANT_LOCAL_WINDOW_CARRIERS: readonly Carriers[] = [
  Carriers.AUSTRALIA_POST,
];

export function usesMerchantLocalManifestWindow(carrier?: Carriers): boolean {
  return !!carrier && MERCHANT_LOCAL_WINDOW_CARRIERS.includes(carrier);
}

/** Just after the 4:30 AM UTC auto-manifest sweep, so swept shipments never reappear. */
const GLOBAL_MANIFEST_CUTOFF_UTC = { hour: 5, minute: 0 };

/**
 * Start of the window the EOD manifest may include: merchant-local midnight for carriers
 * on their own day, otherwise 5 AM UTC just after the global sweep.
 */
export function getMostRecentManifestCutoff({
  carrier,
  merchantTimezone,
}: {
  carrier?: Carriers;
  merchantTimezone: string;
}): Temporal.Instant {
  const isLocal = usesMerchantLocalManifestWindow(carrier);
  const now = Temporal.Now.zonedDateTimeISO(isLocal ? merchantTimezone : "UTC");

  const todaysCutoff = isLocal
    ? now.startOfDay()
    : now.with({
        hour: GLOBAL_MANIFEST_CUTOFF_UTC.hour,
        minute: GLOBAL_MANIFEST_CUTOFF_UTC.minute,
        second: 0,
        millisecond: 0,
        microsecond: 0,
        nanosecond: 0,
      });

  const target =
    now.epochNanoseconds < todaysCutoff.epochNanoseconds
      ? todaysCutoff.subtract({ days: 1 })
      : todaysCutoff;

  return target.toInstant();
}

export function getDynamicEndOfDayManifestFilters({
  carrier,
  address,
  merchantTimezone,
}: {
  carrier?: Carriers;
  address?: string;
  merchantTimezone: string;
}): AdvancedFilterData[] {
  const filters = getStaticEndOfDayManifestFilters({
    carrier,
    merchantTimezone,
  });

  if (carrier) {
    // Our shipping provider (easypost) has multiple names for USPS, depending on what type of account it's under
    // The use just wants to know that we are manifesting USPS shipments, they don't need to know about the different names
    const carriersToSearch =
      carrier === Carriers.USPS
        ? [Carriers.USPS, Carriers.USPS_SHIP]
        : [carrier];
    filters.push({
      name: "boughtRateCarrier",
      type: AdvancedFilterType.ARRAY_TO_STRING,
      value: carriersToSearch,
      operator: ArrayStringFilterOperator.ANY_OF,
    });

    // Easypost does not support pre defined USPS parcels Flat, Card, and Letter
    // https://support.easypost.com/hc/en-us/articles/360041673351-USPS-Manifesting
    // Pre defined packages here: https://docs.easypost.com/carriers/usps-guide#predefined-packages
    if (carrier === Carriers.USPS || carrier === Carriers.USPS_SHIP) {
      // For USPS, we need conditional logic:
      // - If parcel type is 'carrier' and servie is Flat, exclude Card/Letter/Flat pre defined parcels
      // - If parcel type is 'custom', allow any name/service
      // This will be handled by a special compound filter in the search function
      filters.push({
        name: "uspsParcelConstraints",
        type: AdvancedFilterType.STRING,
        value: "NO VALUE",
        operator: StringFilterOperator.EQUALS,
      });
    }

    if (carrier === Carriers.CANADA_POST) {
      filters.push({
        name: "canadaPostManifestRequired",
        type: AdvancedFilterType.BOOLEAN,
        value: true,
        operator: BooleanFilterOperator.IS,
      });
    }
  }

  if (address) {
    filters.push({
      name: "fulfillmentLocation",
      type: AdvancedFilterType.STRING,
      value: address,
      operator: StringFilterOperator.EQUALS,
    });
  }

  if (process.env.NODE_ENV === "production") {
    filters.push({
      name: "shipmentRateMode",
      type: AdvancedFilterType.STRING,
      value: "production",
      operator: StringFilterOperator.EQUALS,
    });
  } else {
    filters.push({
      name: "shipmentRateMode",
      type: AdvancedFilterType.STRING,
      value: "test",
      operator: StringFilterOperator.EQUALS,
    });
  }

  // These providers can return carrier labels that OMS cannot manifest directly.
  filters.push({
    name: "boughtRateProvider",
    type: AdvancedFilterType.ARRAY_TO_STRING,
    value: [RateProvider.TIKTOK, RateProvider.AMAZON_BUY_SHIPPING],
    operator: ArrayStringFilterOperator.NONE_OF,
  });

  return filters;
}

export function getStaticEndOfDayManifestFilters({
  carrier,
  merchantTimezone,
}: {
  carrier?: Carriers;
  merchantTimezone: string;
}): AdvancedFilterData[] {
  return [
    {
      name: "shipmentVoided",
      type: AdvancedFilterType.BOOLEAN,
      value: false,
      operator: BooleanFilterOperator.IS,
    },
    {
      type: AdvancedFilterType.EXISTS,
      name: "shipmentScanFormBatchState",
      value: true,
      operator: ExistsFilterOperator.NOT_EXISTS,
    },
    {
      name: "shipmentScanFormId",
      type: AdvancedFilterType.EXISTS,
      value: true,
      operator: ExistsFilterOperator.NOT_EXISTS,
    },
    {
      name: "status",
      type: AdvancedFilterType.STRING,
      value: FulfillmentGroupStatus.Closed,
      operator: StringFilterOperator.EQUALS,
    },
    {
      name: "labelDate",
      type: AdvancedFilterType.DATE,
      value: KnownDateFilterTimeFrame.CUSTOM,
      customDate: [
        new Date(
          getMostRecentManifestCutoff({ carrier, merchantTimezone }).toString(),
        ),
        new Date(),
      ],
      operator: DateFilterOperator.WITHIN,
    },
  ];
}

export enum ScanFormStatus {
  Creating = "creating",
  Created = "created",
  Error = "failed",
}

export enum BatchState {
  Creating = "creating",
  CreationFailed = "creation_failed",
  Created = "created",
  Purchasing = "purchasing",
  PurchaseFailed = "purchase_failed",
  Purchased = "purchased",
  LabelGenerating = "label_generating",
  LabelGenerated = "label_generated",
}

export function isBatchEventActionState(state: BatchState): boolean {
  const actionStates = [
    BatchState.LabelGenerating,
    BatchState.LabelGenerated,
    BatchState.Created,
    BatchState.Purchased,
  ];
  return actionStates.includes(state);
}

const batchEventSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.null }),
  state: createEnum(BatchState, { enumName: "BatchState" }),
});
export const batchEventSchema: z.ZodType<BatchEvent> = batchEventSchema_;

export interface BatchEvent extends z.infer<typeof batchEventSchema_> {}

const scanFormStatusInfoSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  status: createEnum(ScanFormStatus, { enumName: "ScanFormStatus" }),
});
export const scanFormStatusInfoSchema: z.ZodType<ScanFormStatusInfo> =
  scanFormStatusInfoSchema_;

export interface ScanFormStatusInfo extends z.infer<
  typeof scanFormStatusInfoSchema_
> {}

const shippingProviderScanForm_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  created_at: z.string().meta({ privacy: privacy.nonPii }),
  updated_at: z.string().meta({ privacy: privacy.nonPii }),
  tracking_codes: z
    .array(z.string().meta({ privacy: privacy.external_id }))
    .nullish(),
  address: z
    .object({
      id: z.string().meta({ privacy: privacy.nonPii }),
      name: z.string().meta({ privacy: privacy.person_name }).nullish(),
      street1: z.string().meta({ privacy: privacy.address_line }).nullish(),
      street2: z.string().meta({ privacy: privacy.address_line }).nullish(),
      city: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      state: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      zip: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      country: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      phone: z.string().meta({ privacy: privacy.phone }).nullish(),
      email: z.string().meta({ privacy: privacy.email }).nullish(),
      carrier_facility: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      residential: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
      federal_tax_id: z
        .string()
        .meta({ privacy: privacy.external_id })
        .nullish(),
      state_tax_id: z.string().meta({ privacy: privacy.external_id }).nullish(),
    })
    .nullish(),
  status: createEnum(ScanFormStatus, { enumName: "ScanFormStatus" }),
  message: z.string().meta({ privacy: privacy.free_text }).nullish(),
  form_url: z.string().meta({ privacy: privacy.redact }).nullish(),
  form_file_type: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  batch_id: z.string().meta({ privacy: privacy.nonPii }).nullish(),
});
export const shippingProviderScanForm: z.ZodType<ShippingProviderScanForm> =
  shippingProviderScanForm_;

export interface ShippingProviderScanForm extends z.infer<
  typeof shippingProviderScanForm_
> {}

const RedoScanFormSchema_ = z.object({
  id: z.string().meta({ privacy: privacy.nonPii }),
  /** Coerced: scan_form is a JSONB column, so CRDB returns ISO strings here. */
  externalCreatedAt: z.coerce.date().meta({ privacy: privacy.nonPii }),
  externalUpdatedAt: z.coerce.date().meta({ privacy: privacy.nonPii }),
  trackingCodes: z.array(z.string().meta({ privacy: privacy.external_id })),
  address: z
    .object({
      id: z.string().meta({ privacy: privacy.nonPii }),
      name: z.string().meta({ privacy: privacy.person_name }).nullish(),
      street1: z.string().meta({ privacy: privacy.address_line }).nullish(),
      street2: z.string().meta({ privacy: privacy.address_line }).nullish(),
      city: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      state: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      zip: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      country: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      phone: z.string().meta({ privacy: privacy.phone }).nullish(),
      email: z.string().meta({ privacy: privacy.email }).nullish(),
      carrierFacility: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      residential: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
      federalTaxId: z.string().meta({ privacy: privacy.external_id }).nullish(),
      stateTaxId: z.string().meta({ privacy: privacy.external_id }).nullish(),
    })
    .nullish()
    .meta({ privacy: privacy.structured }),
  status: createEnum(ScanFormStatus, { enumName: "ScanFormStatus" }).meta({
    privacy: privacy.nonPii,
  }),
  message: z.string().meta({ privacy: privacy.free_text }).nullish(),
  formUrl: z.string().meta({ privacy: privacy.redact }),
  formUrl4x6: z.string().meta({ privacy: privacy.redact }).nullish(),
  formFileType: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  batchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  confirmation: z.string().meta({ privacy: privacy.nonPii }).nullish(),
});
export const RedoScanFormSchema: z.ZodType<RedoScanForm> = RedoScanFormSchema_;

export interface RedoScanForm extends z.infer<typeof RedoScanFormSchema_> {}

export enum ManifestType {
  SYNC = "synchronous",
  ASYNC = "asynchronous",
}

const EndOfDayManifestSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
    team: zExt.objectId().meta({ privacy: privacy.nonPii }),
    manifestType: createEnum(ManifestType, { enumName: "ManifestType" })
      .default(ManifestType.ASYNC)
      .meta({ privacy: privacy.nonPii }),
    carrier: createEnum(Carriers, { enumName: "Carriers" }).meta({
      privacy: privacy.nonPii,
    }),
    carrierAccountId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    provider: createEnum(RateProvider, { enumName: "RateProvider" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    pickupId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    groupId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    fulfillmentGroupIds: z
      .array(zExt.objectId().meta({ privacy: privacy.nonPii }))
      .meta({ privacy: privacy.structured }),
    location: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    batch: z
      .object({
        id: z.string().meta({ privacy: privacy.nonPii }),
        state: createEnum(BatchState, { enumName: "BatchState" }).meta({
          privacy: privacy.nonPii,
        }),
        canadaPostTransmittedAt: z.coerce
          .date()
          .nullish()
          .meta({ privacy: privacy.nonPii }),
        canadaPostExcludedShipmentIds: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .optional(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    scanForm: RedoScanFormSchema_.meta({
      privacy: privacy.structured,
    }).nullish(),
    scanFormCreationRequested: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export const EndOfDayManifestSchema: z.ZodType<EndOfDayManifest> =
  EndOfDayManifestSchema_;

export interface EndOfDayManifest extends z.infer<
  typeof EndOfDayManifestSchema_
> {}

const CreateEndOfDayManifestSchema_ = EndOfDayManifestSchema_.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
});
export const CreateEndOfDayManifestSchema: z.ZodType<CreateEndOfDayManifest> =
  CreateEndOfDayManifestSchema_;

export interface CreateEndOfDayManifest extends z.infer<
  typeof CreateEndOfDayManifestSchema_
> {}
